import { useEffect } from "react";

/**
 * MobileInputScrollWatcher
 * ------------------------
 * Document-level listener that keeps focused <input>/<textarea>/[contenteditable]
 * elements visible above the mobile virtual keyboard.
 *
 * We can't easily wrap every existing `<input className="input" />` in the
 * codebase, so instead we install ONE `focusin` listener on the document
 * that fires for every focus event (thanks to focusin's bubbling). On
 * mobile viewports we wait ~260ms (iOS keyboard raise duration) and then
 * `scrollIntoView({ block: "center" })`.
 *
 * On desktop this is a no-op — the media query short-circuits before we
 * do any DOM work.
 *
 * Renders nothing.
 */

const KEYBOARD_DELAY_MS = 260;

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 767.98px)").matches;
}

function isTextualInput(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === "TEXTAREA") return true;
  if (tag === "INPUT") {
    const t = (el.getAttribute("type") || "text").toLowerCase();
    // Skip non-textual inputs where a keyboard usually doesn't pop up.
    return !["checkbox", "radio", "submit", "button", "reset", "range", "color", "file", "hidden"].includes(t);
  }
  return false;
}

export default function MobileInputScrollWatcher() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handler = (event) => {
      if (!isMobileViewport()) return;
      const target = event.target;
      if (!isTextualInput(target)) return;
      window.setTimeout(() => {
        try {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (err) {
          // Older Safari doesn't accept the options object — fall back
          // and log for debugging.
          console.warn("MobileInputScrollWatcher: options unsupported, falling back:", err);
          target.scrollIntoView();
        }
      }, KEYBOARD_DELAY_MS);
    };
    document.addEventListener("focusin", handler);
    return () => document.removeEventListener("focusin", handler);
  }, []);
  return null;
}
